<template>
  <div class="quill-main-container" :style="CSS_VARS" @click="focusEditor">
    <div :id="`container-${quillId}`" class="quill-container custom-scrollbar" ref="editorNode"></div>
    <div v-show="showToolbar" :id="`toolbar-${quillId}`" class="elevation-4" @mousedown.prevent.stop>
      <div class="ql-formats mx-1">
        <!-- Add font style dropdown -->
        <select :id="`font-select-${quillId}`" class="ql-font font-select">
          <option value="times-new-roman">Times New Roman</option>
          <option value="arial">Arial</option>
          <option value="helvetica">Helvetica</option>
        </select>
        <!-- Add font size dropdown -->
        <select :id="`size-select-${quillId}`" class="ql-size size-select">
          <option value="small">10</option>
          <!-- Note a missing, thus falsy value, is used to reset to default -->
          <option selected>14</option>
          <option value="large">18</option>
          <option value="huge">32</option>
        </select>
      </div>
      <!-- Add a bold button -->
      <span class="ql-formats mr-0 px-1 side-borders">
        <button class="ql-bold"></button>
        <button class="ql-italic"></button>
        <button class="ql-underline"></button>
      </span>
      <!-- Text color (options are populated by QuillJS)-->
      <span class="ql-formats mx-1">
        <select :id="`color-select-${quillId}`" class="ql-color"> </select>
        <select :id="`background-select-${quillId}`" class="ql-background"> </select>
      </span>
      <!-- Alignment options ('left' is the falsy default) -->
      <span class="ql-formats mr-0 px-1 alignment side-borders">
        <select id="align-select" class="ql-align">
          <option value=""><button class="ql-align" value=""></button></option>
          <!-- Note a missing, thus falsy value, is used to reset to default -->
          <option value="center"><button class="ql-align" value="center"></button></option>
          <option value="right"><button class="ql-align" value="right"></button></option>
        </select>
        <button class="ql-list" value="bullet"></button>
        <button class="ql-list" value="ordered"></button>
      </span>
      <!-- Indent options -->
      <span class="ql-formats mx-1">
        <button class="ql-indent" value="-1"></button>
        <button class="ql-indent" value="+1"></button>
      </span>
      <!-- Add subscript and superscript buttons -->
      <span class="ql-formats side-borders mr-0 px-1">
        <button class="ql-script" value="super"></button>
        <button class="ql-script" value="sub"></button>
      </span>
      <!-- Clear formatting -->
      <span class="ql-formats mx-1">
        <button class="ql-clean"></button>
      </span>
    </div>
  </div>
</template>

<script>
import Quill from 'quill';

const FontAttributor = Quill.import('formats/font');
FontAttributor.whitelist = ['times-new-roman', 'arial', 'helvetica'];
Quill.register(FontAttributor, true);

export default {
  name: 'EcsQuillEditor',

  props: {
    value: {
      default: '',
      type: String
    },
    quillId: {
      type: String,
      required: true
    },
    minHeight: {
      type: String,
      default: '128px'
    }
  },

  data() {
    return {
      editorContent: null,
      editorInstance: null,
      editorOpts: {
        modules: {
          toolbar: `#toolbar-${this.quillId}`
        },
        theme: 'snow'
      },
      showToolbar: false
    };
  },

  computed: {
    CSS_VARS() {
      const color = this.$vuetify.theme.dark ? '#fff' : '#000';
      return {
        '--ql-color': `${color}`,
        minHeight: this.minHeight
      };
    }
  },

  watch: {
    value(newVal) {
      // Only update the content if it's changed from an external source
      // or else it'll act weird when you try to type anything
      if (newVal !== this.editorContent && !this.disabled) {
        this.editorInstance.pasteHTML(newVal);
        this.editorInstance.setSelection(newVal.length, 0);
      }
    }
  },

  mounted() {
    this.initializeEditor();
  },

  beforeDestroy() {
    // Turn off all listeners set on text-change
    this.editorInstance.off('text-change');
  },

  methods: {
    initializeEditor() {
      // Set initial content that's going to be picked up by Quill
      this.$refs.editorNode.innerHTML = this.value;
      // Create the Quill instance
      this.editorInstance = new Quill(this.$refs.editorNode, this.editorOpts);
      if (this.disabled) {
        this.editorInstance.disable();
      }
      // Setup handler for whenever things change inside Quill
      this.editorInstance.on('text-change', this.onEditorContentChange);
      // Save any initial content to this.editorContent
      this.setEditorContent();
      this.editorInstance.on('selection-change', range => {
        if (!range) {
          this.onBlur();
        } else {
          this.onFocus();
        }
      });
    },
    onEditorContentChange() {
      // Whenever we change anything, update this.editorContent
      this.setEditorContent();
      // Then emit its value as input so we have a working v-model
      // This $emit will be catched up in the watch:value
      // that's why we guard against calling pasteHTML
      // calling that function while we are typing is undesirable
      this.$emit('input', this.editorContent);
    },
    setEditorContent() {
      this.editorContent = this.editorInstance.getText().trim() ? this.editorInstance.root.innerHTML : '';
    },
    onFocus() {
      this.showToolbar = true;
    },
    onBlur() {
      this.showToolbar = false;
    },
    focusEditor() {
      this.editorInstance.focus();
    }
  }
};
</script>

<style src="./styles.sass" lang="sass"></style>
<style src="./styles.scss" lang="scss"></style>
